import { Filter, FilterObject } from '@insurancemenu/imenu-components-lib/dist/types/filterSidebarTypings';
import {
  BrowsingProduct,
  Portfolio,
  Product,
  ProductLineAttr,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { makeAutoObservable } from 'mobx';
import { RootStore } from 'store/rootStore';

export default class BrowsingFilterStore {
  rootStore: RootStore;
  loading = false;

  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  filterPortfoliosBy(list: Portfolio[], filters: Filter, attrs: ProductLineAttr[]): Portfolio[] {
    let updatedPortfolios = [...list];
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'tag') {
        updatedPortfolios = this.filterByTag(value as string[], updatedPortfolios);
      }
      if (key === 'rates') {
        updatedPortfolios = this.filterByRates(value as FilterObject[], updatedPortfolios);
      }
      if (key === 'carrier') {
        updatedPortfolios = this.filterByCarrier(value as string[], updatedPortfolios);
      }

      if (key !== 'rates' && key !== 'tag' && key !== 'carrier') {
        updatedPortfolios = this.filterByType(key, value, updatedPortfolios, attrs);
      }
    });
    return updatedPortfolios;
  }

  // Show portfolio if it has one of tag which is selected in filter
  // eslint-disable-next-line class-methods-use-this
  private filterByTag(tags: string[], list: Portfolio[]): Portfolio[] {
    if (!tags.length || !list.length) return list;

    return list.filter((portfolio) => {
      const hasOneOfTag = tags.some((tagId) => portfolio.marketingTagIds.includes(tagId));

      if (hasOneOfTag) return portfolio;
      return null;
    });
  }

  // Show portfolio if it has one of carrier which is selected in filter
  // eslint-disable-next-line class-methods-use-this
  private filterByCarrier(carrierIds: string[], portfolios: Portfolio[]): Portfolio[] {
    if (!carrierIds.length || !portfolios.length) return portfolios;
    return portfolios.filter((portfolio) => {
      if (portfolio.products.some(({ carrier }) => carrierIds.includes(carrier.id))) {
        return portfolio;
      }
      return null;
    });
  }

  // eslint-disable-next-line class-methods-use-this
  private filterByRates(filterRates: FilterObject[], list: Portfolio[]): Portfolio[] {
    if (!filterRates.length || !list.length) return list;
    const filteredList: Portfolio[] = [];

    list.forEach((portfolio) => {
      // search in products list
      const filteredProducts = portfolio.products.filter((product) => {
        // get only rates than fit the filter
        const ratesInRange = product.bcDependentData[0].rates.filter(({ displayKey, value }) => {
          const currentFilterRateObj = filterRates.find((rate) => rate[displayKey]);

          if (currentFilterRateObj) {
            const rateValues = currentFilterRateObj[displayKey];

            if (+value >= rateValues[0] && +value <= rateValues[1]) {
              return { displayKey, value };
            }
          }
          // currentFilterRateObj can be undefined if rate with this displayKey is not selected in filter
          return null;
        });

        if (
          ratesInRange.length === filterRates.length ||
          ratesInRange.length === product.bcDependentData[0].rates.length // Product should not be filtered out, if it has different amount of rates from the filters, but still all of them pass
        ) {
          return product;
        }
        return null;
      });

      // hide portfolio if all products are inactive or highlight inactive in multi product portfolio
      const updatedPortfolio = this.filterProducts(portfolio, filteredProducts);
      if (updatedPortfolio) {
        filteredList.push(updatedPortfolio);
      }
    });

    return filteredList;
  }

  private filterByType<T>(
    displayName: string,
    filterValue: T, // possible variants string[] | number[] | boolean,
    list: Portfolio[],
    productLineAttrs: ProductLineAttr[],
  ): Portfolio[] {
    const currentAttr = productLineAttrs.find((attr) => attr.displayName === displayName); // attr whole object
    const type = currentAttr?.type;

    return list
      .map((portfolio) => {
        // search in products list
        const filteredProducts = portfolio.products.filter((product) => {
          // find attribute in product object that was changed in filter
          const currentProductAttr = product.bcDependentData[0].productAttributes.find(
            (productAttr) => productAttr.attributeId === currentAttr?.id,
          );

          if (!currentProductAttr) return null;

          let isValueFitTheFilter;
          if (type === 'booleanCheckbox' && typeof filterValue === 'boolean') {
            isValueFitTheFilter = filterValue.toString() === currentProductAttr.attributeValue?.trim();
          }
          if (type === 'valueCheckbox' && Array.isArray(filterValue)) {
            isValueFitTheFilter =
              filterValue.length === 0 ? true : filterValue.includes(currentProductAttr.attributeValue?.trim() || '');
          }
          if (type === 'rangeValue' && Array.isArray(filterValue)) {
            isValueFitTheFilter = this.checkIsValueInRange(
              currentProductAttr.attributeValue?.trim() || '',
              filterValue,
            );
          }
          if (isValueFitTheFilter) return product;
          return null;
        });

        // highlight inactive products in multi product portfolio or
        // hide portfolio if all products are inactive
        return this.filterProducts(portfolio, filteredProducts);
      })
      .filter((portfolio) => portfolio !== null);
  }

  // eslint-disable-next-line class-methods-use-this
  private checkIsValueInRange(attributeValue: string, filterRange: number[]): boolean {
    return +attributeValue >= filterRange[0] && +attributeValue <= filterRange[1];
  }

  // for multi product portfolio
  // eslint-disable-next-line class-methods-use-this
  private highlightInactiveProducts(portfolio: Portfolio, filteredProducts: Product[]): Portfolio | null {
    const allProductIds = portfolio.products.map(({ imProductId }) => imProductId);
    const activeProductIds = filteredProducts.map(({ imProductId }) => imProductId);

    const updatedProducts = portfolio.products.map((product): BrowsingProduct => {
      if (activeProductIds.includes(product.imProductId)) {
        return { ...product, disabled: false };
      }
      return { ...product, disabled: true };
    });

    const isEveryProductDisabled = updatedProducts.every(({ disabled }) => disabled);

    if (activeProductIds.length !== allProductIds.length && !isEveryProductDisabled) {
      return { ...portfolio, products: updatedProducts };
    }

    return null;
  }

  private filterProducts(portfolio: Portfolio, filteredProducts: Product[]): Portfolio | null {
    // highlight inactive products in multi product portfolio
    if (portfolio.products.length > 1 && portfolio.products.length !== filteredProducts.length) {
      const updatedPortfolio = this.highlightInactiveProducts(portfolio, filteredProducts);
      if (updatedPortfolio) return updatedPortfolio;
      return null;
    }

    return portfolio.products.length === filteredProducts.length ? portfolio : null;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
